import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/supabase/client";

const REFRESH_INTERVAL = 30000;
const POSSIBLE_TIMESTAMP_KEYS = [
  "last_sync",
  "last_sync_at",
  "last_synced_at",
  "last_synced",
  "last_successful_sync",
  "synced_at",
  "updated_at",
  "created_at",
];

function formatTimestamp(value: string | Date | null | undefined) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

interface StatusPillProps {
  label: string;
  state: string;
}

function StatusPill({ label, state }: StatusPillProps) {
  let classes = "border-slate-200 bg-slate-100 text-slate-700";
  let text = "Checking…";

  if (state === "ok") {
    classes = "border-emerald-200 bg-emerald-50 text-emerald-800";
    text = "Reachable";
  } else if (state === "down") {
    classes = "border-rose-200 bg-rose-50 text-rose-800";
    text = "Unavailable";
  }

  return (
    <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium ${classes}`}>
      <span className="font-semibold">{label}</span>
      <span>{text}</span>
    </span>
  );
}

export default function SystemStatus() {
  const [status, setStatus] = useState("checking");
  const [supabaseState, setSupabaseState] = useState("checking");
  const [functionsState, setFunctionsState] = useState("checking");
  const [lastSync, setLastSync] = useState<string | null>(null);
  const [lastCheckedAt, setLastCheckedAt] = useState<string | null>(null);

  const isMounted = useRef(true);
  const lastKnownSync = useRef<string | null>(null);
  const checkingRef = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    const controller = new AbortController();

    const checkStatus = async () => {
      if (checkingRef.current) return;
      checkingRef.current = true;

      try {
        let nextSupabaseState = "down";
        let nextFunctionsState = "down";
        let nextLastSync = null;

        try {
          const { data, error } = await supabase.from("health").select("*").limit(1);
          if (error) throw error;
          if (Array.isArray(data) && data.length > 0) {
            nextSupabaseState = "ok";
            const record = data[0] as Record<string, unknown>;
            for (const key of POSSIBLE_TIMESTAMP_KEYS) {
              if (record && record[key]) {
                nextLastSync = record[key] as string;
                break;
              }
            }
          } else {
            nextSupabaseState = "ok";
          }
        } catch (error) {
          console.warn("SystemStatus: supabase health check failed", error);
          nextSupabaseState = "down";
        }

        try {
          const CORE_API = import.meta.env.VITE_CORE_API_URL || '';
          const response = await fetch(`${CORE_API}/health`, {
            method: "GET",
            headers: { Accept: "application/json" },
            cache: "no-store",
            signal: controller.signal,
          });
          if (response.ok) {
            nextFunctionsState = "ok";
          } else {
            nextFunctionsState = "down";
          }
        } catch (error: unknown) {
          if (error instanceof Error && error.name === "AbortError") {
            return;
          }
          console.warn("SystemStatus: functions health check failed", error);
          nextFunctionsState = "down";
        }

        if (!isMounted.current) {
          return;
        }

        setSupabaseState(nextSupabaseState);
        setFunctionsState(nextFunctionsState);

        if (nextLastSync) {
          lastKnownSync.current = nextLastSync;
          setLastSync(nextLastSync);
        } else if (lastKnownSync.current) {
          setLastSync(lastKnownSync.current);
        }

        const overallStatus =
          nextSupabaseState === "ok" && nextFunctionsState === "ok" ? "healthy" : "degraded";
        setStatus(overallStatus);
        setLastCheckedAt(new Date().toISOString());
      } catch (error) {
        console.error("SystemStatus: unexpected status check failure", error);
      } finally {
        checkingRef.current = false;
      }
    };

    checkStatus();
    const intervalId = setInterval(checkStatus, REFRESH_INTERVAL);

    return () => {
      isMounted.current = false;
      controller.abort();
      clearInterval(intervalId);
      checkingRef.current = false;
    };
  }, []);

  const bannerStyles = useMemo(() => {
    if (status === "healthy") {
      return "border-emerald-200 bg-emerald-50 text-emerald-900";
    }
    if (status === "degraded") {
      return "border-amber-200 bg-amber-50 text-amber-900";
    }
    return "border-sky-200 bg-sky-50 text-sky-900";
  }, [status]);

  const formattedSync = formatTimestamp(lastSync);
  const formattedCheckedAt = formatTimestamp(lastCheckedAt);

  return (
    <div className={`mx-auto my-3 w-full max-w-4xl rounded-xl border px-4 py-3 text-sm shadow-sm ${bannerStyles}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <strong className="font-semibold">System status:</strong>{" "}
          {status === "healthy" ? "Healthy" : status === "degraded" ? "Degraded" : "Checking…"}
          {formattedCheckedAt ? (
            <span className="ml-2 text-xs opacity-80">Checked {formattedCheckedAt}</span>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          <StatusPill label="Supabase" state={supabaseState} />
          <StatusPill label="API" state={functionsState} />
          <span className="inline-flex items-center rounded-full border border-slate-200 bg-white/50 px-3 py-1 text-xs font-medium text-slate-700">
            <span className="font-semibold">Last sync</span>
            <span className="ml-2">{formattedSync || "—"}</span>
          </span>
        </div>
      </div>
      {status === "degraded" ? (
        <p className="mt-2 text-xs font-medium opacity-80">
          One or more services are unreachable. Some actions may be delayed until connectivity is restored.
        </p>
      ) : null}
    </div>
  );
}
